<template>
  <div>

    <!-- ── Header bar ──────────────────────────────────────────────────── -->
    <div class="d-flex align-center px-2 py-2">
      <v-icon class="mr-2">mdi-pencil</v-icon>
      <span class="text-subtitle-1 font-weight-medium">Edit Billing Record</span>
      <v-chip
        v-if="originalRecord"
        color="info"
        text-color="white"
        x-small
        class="ml-2"
      >
        {{ originalRecord._id || originalRecord.id }}
      </v-chip>
      <v-spacer />
      <span class="text-caption grey--text mr-3">
        Step {{ editStep }} of 2 —
        {{ editStep === 1 ? 'Period &amp; Data' : 'Review &amp; Save' }}
      </span>
      <v-btn text x-small @click="cancelEdit">
        <v-icon x-small left>mdi-close</v-icon>Cancel
      </v-btn>
    </div>

    <v-divider />

    <!-- Loading -->
    <div v-if="loading && !originalRecord" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" size="48" />
      <div class="text-body-2 mt-3 grey--text">Loading billing record…</div>
    </div>

    <!-- Error -->
    <v-alert
      v-if="error"
      type="error"
      dense
      dismissible
      class="ma-3"
      @input="clearError"
    >
      {{ error }}
    </v-alert>

    <template v-if="originalRecord">

      <!-- ── STEP 1: Period & Data ──────────────────────────────────── -->
      <div v-show="editStep === 1" class="pa-4">

        <!-- Record meta -->
        <div class="d-flex align-center mb-3">
          <span class="text-caption grey--text">
            Created {{ formatDateTime(originalRecord.createdAt) }}
          </span>
          <span v-if="originalRecord.updatedAt" class="text-caption grey--text ml-3">
            · Modified {{ formatDateTime(originalRecord.updatedAt) }}
          </span>
        </div>

        <!-- Period selector (edit mode — pre-populated) -->
        <div class="field-section-label">Billing Period</div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-menu v-model="startDateMenu" :close-on-content-click="false"
              transition="scale-transition" offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="formattedStartDate"
                  label="Start Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly dense outlined hide-details
                  v-bind="attrs" v-on="on"
                />
              </template>
              <v-date-picker v-model="period.startDate" @input="onPeriodInput('start')" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6">
            <v-menu v-model="endDateMenu" :close-on-content-click="false"
              transition="scale-transition" offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="formattedEndDate"
                  label="End Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly dense outlined hide-details
                  v-bind="attrs" v-on="on"
                />
              </template>
              <v-date-picker v-model="period.endDate" @input="onPeriodInput('end')" />
            </v-menu>
          </v-col>
        </v-row>

        <div class="field-section-label mt-4">Consumption Source</div>
        <v-radio-group v-model="dataEntryMode" row hide-details class="mt-0 mb-2">
          <v-radio label="Automatic" value="automatic"
            :disabled="!period.startDate || !period.endDate" />
          <v-radio label="Manual" value="manual" />
        </v-radio-group>

        <v-btn
          v-if="dataEntryMode === 'automatic'"
          small color="primary" class="mb-2"
          :disabled="!period.startDate || !period.endDate || loadingConsumption"
          :loading="loadingConsumption"
          @click="fetchConsumptionData"
        >
          <v-icon left small>mdi-download</v-icon>
          Fetch Consumption Data
        </v-btn>

        <!-- Manual data form -->
        <template v-if="dataEntryMode === 'manual' && !isTimeOfUseMode">
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="manualReading.initial"
                label="Initial Reading (kWh)"
                type="number" step="0.1"
                dense outlined hide-details
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="manualReading.final"
                label="Final Reading (kWh)"
                type="number" step="0.1"
                dense outlined hide-details
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                :value="calculatedConsumption"
                label="Total (kWh)"
                readonly dense outlined hide-details
              />
            </v-col>
          </v-row>
          <div class="d-flex justify-end mt-2">
            <v-btn small color="primary"
              :disabled="calculatedConsumption < 0 || !period.startDate || !period.endDate"
              @click="confirmManualConsumption">
              <v-icon left small>mdi-check</v-icon>Confirm
            </v-btn>
          </div>
        </template>

        <template v-if="dataEntryMode === 'manual' && isTimeOfUseMode">
          <v-simple-table dense class="tou-table mb-2">
            <template #default>
              <thead>
                <tr>
                  <th>Period</th><th>Initial</th><th>Final</th><th class="text-right">Units</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in touUnitLines" :key="line.key">
                  <td class="text-caption font-weight-medium">{{ line.label }}</td>
                  <td>
                    <v-text-field
                      v-model.number="manualData.timeOfUseUnits[line.key].initialReading"
                      type="number" step="0.01"
                      dense outlined hide-details class="tou-input"
                      @input="syncTouLine(line.key)"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="manualData.timeOfUseUnits[line.key].finalReading"
                      type="number" step="0.01"
                      dense outlined hide-details class="tou-input"
                      @input="syncTouLine(line.key)"
                    />
                  </td>
                  <td class="text-right text-caption font-weight-bold">
                    {{ formatTouUnits(line.key) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div class="text-caption grey--text text-right mb-2">
            Total: <strong>{{ formattedTouTotal }} kWh</strong>
          </div>
        </template>

        <v-alert v-if="consumptionData" dense text type="success"
          class="mb-0 mt-2 py-1" style="font-size:0.8rem;">
          <v-icon left small>mdi-check-circle</v-icon>
          {{ formattedTotalConsumption }} kWh confirmed
          <span class="ml-2 grey--text">
            ({{ consumptionData.dataSource === 'automatic' ? 'Auto' : 'Manual' }}
            Â· {{ billingDurationDays }}d)
          </span>
        </v-alert>

        <v-alert v-if="consumptionError" dense type="error"
          dismissible class="mt-2" @input="consumptionError = null">
          {{ consumptionError }}
        </v-alert>

        <template v-if="consumptionData">
          <v-divider class="my-4" />
          <div class="field-section-label">Bill Details</div>
          <v-row dense>
            <v-col cols="12" sm="6" v-if="showSingleKvaDemandInput">
              <v-text-field
                v-model.number="manualData.kvaDemand"
                label="kVA Demand"
                type="number" step="0.01" min="0"
                suffix="kVA" dense outlined hide-details
                @input="markDraftForRecalculation"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="manualData.actualBillAmount"
                label="Actual Bill Amount"
                type="number" step="0.01" min="0"
                :suffix="$currencySymbol" dense outlined hide-details
                @input="markDraftForRecalculation"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="manualData.actualKwhFromBill"
                label="Actual kWh from Bill"
                type="number" step="0.1" min="0"
                suffix="kWh" dense outlined hide-details
                @input="markDraftForRecalculation"
              />
            </v-col>
            <v-col cols="12" sm="6" v-if="showManualKvaDemandCharges">
              <v-text-field
                v-model.number="manualData.kvaDemandCharges"
                label="kVA Demand Charges"
                type="number" step="0.01" min="0"
                :suffix="$currencySymbol" dense outlined hide-details
                @input="markDraftForRecalculation"
              />
            </v-col>
          </v-row>

          <template v-if="showTimeOfUseDemandSplit">
            <div class="field-section-label mt-4">Maximum Demand</div>
            <v-simple-table dense class="tou-table mb-1">
              <template #default>
                <thead>
                  <tr>
                    <th>Period</th><th>kW</th><th class="text-right">Charge</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in touDemandLines" :key="line.key">
                    <td class="text-caption font-weight-medium">
                      {{ line.label }}
                      <span class="grey--text ml-1">@{{ formatRate(line.rate) }}</span>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="manualData.timeOfUseDemand[line.key].kw"
                        type="number" step="0.01" min="0"
                        dense outlined hide-details class="tou-input"
                        @input="syncDemandLine(line.key, line.rate)"
                      />
                    </td>
                    <td class="text-right text-caption font-weight-bold">
                      {{ formatDemandCharge(line.key) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="text-caption grey--text text-right mb-2">
              Total demand charges:
              <strong>{{ formattedTouDemandTotal }} {{ $currencySymbol }}</strong>
            </div>
          </template>

          <v-textarea
            v-model="manualData.notes"
            label="Notes (optional)"
            rows="2" dense outlined hide-details class="mt-3"
          />
        </template>

        <v-divider class="mt-4" />
        <div class="d-flex justify-end pa-3">
          <v-btn
            color="primary"
            :disabled="!consumptionData
              || !consumptionData.billingPeriod
              || manualData.actualBillAmount === null
              || manualData.actualBillAmount === undefined
              || manualData.actualBillAmount === ''"
            :loading="recalculateInFlight"
            @click="goToReviewStep"
          >
            Review &amp; Save
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- ── STEP 2: Review & Save ──────────────────────────────────── -->
      <div v-show="editStep === 2">

        <!-- Recalculating -->
        <div v-if="recalculateInFlight" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="text-body-2 mt-3 grey--text">Recalculating bill…</div>
        </div>

        <template v-else-if="calculatedBill && billBreakdownData">
          <BillBreakdownDisplay
            :bill-data="billBreakdownData"
            use-current-rates
          />
          <BillSaveComponent
            ref="saveComponent"
            :edit-mode="true"
            :bill-data="billBreakdownData"
            :original-record="originalRecord"
            :updated-record="updatedRecord"
            :calculated-bill="calculatedBill"
            :consumption-data="consumptionData"
            :manual-data="manualData"
            @save-success="onSaveSuccess"
            @save-error="onSaveError"
            @show-message="showMessage"
          />
        </template>

        <v-divider />
        <div class="pa-3">
          <v-btn text small @click="editStep = 1">
            <v-icon left small>mdi-arrow-left</v-icon>Back
          </v-btn>
        </div>
      </div>

    </template>

    <div style="display:none">
      <BillingPeriodSelector
        ref="periodSelector"
        :edit-mode="true"
        :initial-data="periodSelectorData"
        @consumption-data-retrieved="onConsumptionDataRetrieved"
        @show-message="showMessage"
      />
    </div>

    <!-- Success Dialog -->
    <v-dialog v-model="showSuccessDialog" max-width="480">
      <v-card>
        <v-card-title class="success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          Record Updated
        </v-card-title>
        <v-card-text>
          <div v-if="originalRecord">
            Record <strong>{{ originalRecord._id || originalRecord.id }}</strong>
            has been updated successfully.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="goToDetail">View Record</v-btn>
          <v-btn text @click="goToHistory">History</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
/* eslint-disable max-len */
import { mapState, mapActions, mapGetters } from 'vuex';
import BillingPeriodSelector from '../components/billing/BillingPeriodSelector.vue';
import BillBreakdownDisplay from '../components/billing/BillBreakdownDisplay.vue';
import BillSaveComponent from '../components/billing/BillSaveComponent.vue';
import costCategoryMixin from '../mixins/costCategoryMixin';
import { resolveBillingMode, isTimeOfUseBillingMode } from '../utils/billingMode';
import { calculateTimeOfUseBillingRate, calculateTimeOfUseTotalCalculated, getTimeOfUseSubtotalBasis } from '../utils/timeOfUseBilling';
import appConfig from '../config/appConfig';

export default {
  name: 'BillingEdit',
  mixins: [costCategoryMixin],

  components: {
    BillingPeriodSelector,
    BillBreakdownDisplay,
    BillSaveComponent,
  },

  props: {
    id: {
      type: String,
      required: true,
      validator(value) {
        return value && value.trim().length > 0;
      },
    },
  },

  data() {
    return {
      currentStep: '1',
      editStep: 1,
      period: { startDate: null, endDate: null },
      startDateMenu: false,
      endDateMenu: false,
      dataEntryMode: 'manual',
      loadingConsumption: false,
      consumptionError: null,
      manualReading: { initial: 0, final: 0 },
      originalRecord: null,
      consumptionData: null,
      manualData: {
        kvaDemand: null,
        actualBillAmount: null,
        kvaDemandCharges: 0,
        actualKwhFromBill: null,
        timeOfUseUnits: null,
        timeOfUseDemand: null,
        oilSurchargeRate: 0,
        notes: '',
      },
      calculatedBill: null,
      showSuccessDialog: false,
      recalculateTimer: null,
      recalculateInFlight: false,
      recalculateQueued: false,
      needsRecalculation: false,
    };
  },

  computed: {
    ...mapState('billing', ['loading', 'error']),
    ...mapGetters('billing', ['isEditMode']),

    formattedStartDate() {
      return this.period.startDate ? this.$formatDate(this.period.startDate) : '';
    },

    formattedEndDate() {
      return this.period.endDate ? this.$formatDate(this.period.endDate) : '';
    },

    billingDurationDays() {
      if (!this.period.startDate || !this.period.endDate) return 0;
      const diff = Math.abs(new Date(this.period.endDate) - new Date(this.period.startDate));
      return Math.floor(diff / 86400000) + 1;
    },

    calculatedConsumption() {
      const initial = parseFloat(this.manualReading.initial) || 0;
      const final = parseFloat(this.manualReading.final) || 0;
      return final - initial;
    },

    formattedTotalConsumption() {
      return (parseFloat(this.consumptionData?.totalConsumption) || 0).toLocaleString();
    },

    touUnitLines() {
      const labels = {
        onPeak: 'ON PEAK',
        offPeak: 'OFF PEAK',
        sunday: 'SAT/SUN',
        misc: 'MISC',
      };
      return Object.keys(this.manualData.timeOfUseUnits || {})
        .map((key) => ({ key, label: labels[key] || key.toUpperCase() }));
    },

    touDemandLines() {
      const labels = {
        onPeak: 'ON PEAK',
        offPeak: 'OFF PEAK',
        sunday: 'SUNDAY',
      };
      return Object.entries(this.manualData.timeOfUseDemand || {})
        .map(([key, line]) => ({
          key,
          label: labels[key] || key.toUpperCase(),
          rate: parseFloat(line?.rate) || 0,
        }));
    },

    touTotalUnits() {
      return Object.values(this.manualData.timeOfUseUnits || {})
        .reduce((sum, line) => sum + (parseFloat(line?.units) || 0), 0);
    },

    formattedTouTotal() {
      return this.touTotalUnits.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formattedTouDemandTotal() {
      return this.timeOfUseDemandChargeTotal.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    showSingleKvaDemandInput() {
      return !this.isTimeOfUseMode || !this.shouldSplitDemandByTouPeriod;
    },

    showManualKvaDemandCharges() {
      return !this.isTimeOfUseMode && !this.usesCalculatedDemandCharge;
    },

    showTimeOfUseDemandSplit() {
      return this.isTimeOfUseMode && this.shouldSplitDemandByTouPeriod;
    },

    periodSelectorData() {
      const billingPeriod = this.consumptionData?.billingPeriod || this.originalRecord?.billingPeriod;
      const consumptionData = this.buildConsumptionDataForSave();
      const recordId = this.originalRecord?._id || this.originalRecord?.id;

      if (!this.originalRecord || !billingPeriod || !consumptionData) return null;

      return {
        _id: recordId, // Include record ID for overlap exclusion
        billingPeriod,
        consumptionData: {
          ...consumptionData,
          touBreakdown: this.manualData.timeOfUseUnits || consumptionData?.touBreakdown || {},
          demandBreakdown: this.manualData.timeOfUseDemand || consumptionData?.demandBreakdown || {},
        },
      };
    },

    manualDataFormData() {
      if (!this.originalRecord) return null;

      return {
        kvaDemand: this.manualData.kvaDemand ?? this.originalRecord.kvaDemand ?? 0,
        actualBillAmount: this.manualData.actualBillAmount ?? this.originalRecord.actualBillAmount ?? 0,
        kvaDemandCharges: this.manualData.kvaDemandCharges ?? this.originalRecord.kvaDemandCharges ?? 0,
        actualKwhFromBill: this.manualData.actualKwhFromBill ?? this.originalRecord.actualKwhFromBill ?? 0,
        timeOfUseUnits: this.manualData.timeOfUseUnits || this.getTimeOfUseUnitsFrom(this.originalRecord) || null,
        timeOfUseDemand: this.manualData.timeOfUseDemand || this.getTimeOfUseDemandFrom(this.originalRecord) || null,
        oilSurchargeRate: this.manualData.oilSurchargeRate ?? this.originalRecord.oilSurchargeRate ?? 0,
        notes: this.manualData.notes ?? this.originalRecord.notes ?? '',
      };
    },

    updatedRecord() {
      if (!this.originalRecord || !this.calculatedBill || !this.calculatedBill.calculatedCharges) return null;

      const variance = (this.manualData.actualBillAmount || 0) - this.displayedCalculatedAmount;
      const consumptionData = this.buildConsumptionDataForSave();
      const unitCostForBilling = this.calculateUnitCostForBilling(this.calculatedBill);
      const calculatedCharges = {
        ...this.calculatedBill.calculatedCharges,
        totalCalculated: this.shouldUseBillingUnitCost
          ? calculateTimeOfUseTotalCalculated(this.calculatedBill)
          : this.calculatedBill.calculatedCharges?.totalCalculated,
      };

      return {
        ...this.originalRecord,
        billingPeriod: consumptionData?.billingPeriod || this.originalRecord.billingPeriod,
        consumptionData,
        kvaDemand: this.effectiveKvaDemand,
        kvaDemandCharges: this.effectiveKvaDemandCharges,
        actualBillAmount: this.manualData.actualBillAmount || 0,
        actualKwhFromBill: this.manualData.actualKwhFromBill || 0,
        calculatedCharges,
        timeOfUseUnits: this.manualData.timeOfUseUnits || consumptionData?.touBreakdown || {},
        timeOfUseDemand: this.manualData.timeOfUseDemand || consumptionData?.demandBreakdown || {},
        oilSurchargeRate: this.manualData.oilSurchargeRate || 0,
        serviceCharge: this.calculatedBill.calculatedCharges?.SERVICE_CHARGE || this.originalRecord.serviceCharge || 0,
        unitCostForBilling,
        calculationMode: this.isTimeOfUseMode ? 'TIME_OF_USE' : this.billingMode,
        variance,
        ratesUsed: this.calculatedBill.ratesUsed || [],
        notes: this.manualData.notes || '',
        updatedAt: new Date(),
      };
    },

    billBreakdownData() {
      if (!this.calculatedBill || !this.consumptionData) return null;

      const calculatedCharges = {
        ...this.calculatedBill.calculatedCharges,
        totalCalculated: this.shouldUseBillingUnitCost
          ? calculateTimeOfUseTotalCalculated(this.calculatedBill)
          : this.calculatedBill.calculatedCharges?.totalCalculated,
      };

      return {
        billingPeriod: this.consumptionData.billingPeriod,
        consumptionData: this.buildConsumptionDataForSave(),
        kvaDemand: this.effectiveKvaDemand,
        kvaDemandCharges: this.effectiveKvaDemandCharges,
        actualBillAmount: this.manualData.actualBillAmount || 0,
        actualKwhFromBill: this.manualData.actualKwhFromBill || 0,
        calculatedCharges,
        timeOfUseUnits: this.manualData.timeOfUseUnits || this.consumptionData?.touBreakdown || {},
        timeOfUseDemand: this.manualData.timeOfUseDemand || this.consumptionData?.demandBreakdown || {},
        oilSurchargeRate: this.manualData.oilSurchargeRate || 0,
        serviceCharge: this.calculatedBill.calculatedCharges?.SERVICE_CHARGE || this.originalRecord?.serviceCharge || 0,
        calculationMode: this.isTimeOfUseMode ? 'TIME_OF_USE' : this.billingMode,
        unitCostForBilling: this.calculateUnitCostForBilling(this.calculatedBill),
        variance: (this.manualData.actualBillAmount || 0) - this.displayedCalculatedAmount,
        ratesUsed: this.calculatedBill.ratesUsed || [],
      };
    },

    shouldUseBillingUnitCost() {
      return appConfig.getBillingCapabilities().useTouUnitCost === true
        && (
          this.isTimeOfUseMode
          || this.calculatedBill?.calculationMode === 'TIME_OF_USE'
        );
    },

    stepNumber() {
      return Number(this.currentStep || 1);
    },

    billingMode() {
      return resolveBillingMode({
        activeCategories: this.costCategories,
        record: this.originalRecord,
        consumptionData: this.consumptionData,
        manualData: this.manualData,
        calculatedBill: this.calculatedBill,
      });
    },

    isTimeOfUseMode() {
      return isTimeOfUseBillingMode(this.billingMode);
    },

    usesCalculatedDemandCharge() {
      const calculatedCharges = this.calculatedBill?.calculatedCharges || this.originalRecord?.calculatedCharges || {};
      return Object.prototype.hasOwnProperty.call(calculatedCharges, 'KVA_DEMAND_CHARGE')
        || Object.prototype.hasOwnProperty.call(calculatedCharges, 'KVA_PER_UNIT_CHARGE');
    },

    timeOfUseTotalDemand() {
      const timeOfUseDemand = this.manualData.timeOfUseDemand || this.consumptionData?.demandBreakdown || {};
      return Object.values(timeOfUseDemand).reduce((sum, line) => (
        sum + (parseFloat(line?.kw) || 0)
      ), 0);
    },

    timeOfUseDemandChargeTotal() {
      const timeOfUseDemand = this.manualData.timeOfUseDemand || this.consumptionData?.demandBreakdown || {};
      return Object.values(timeOfUseDemand).reduce((sum, line) => {
        const kw = Math.max(parseFloat(line?.kw) || 0, 0);
        const rate = parseFloat(line?.rate) || 0;
        return sum + (kw * rate);
      }, 0);
    },

    shouldSplitDemandByTouPeriod() {
      return appConfig.getBillingCapabilities().splitDemandByTouPeriod === true;
    },

    effectiveKvaDemand() {
      return this.isTimeOfUseMode && this.shouldSplitDemandByTouPeriod
        ? this.timeOfUseTotalDemand
        : (parseFloat(this.manualData.kvaDemand) || 0);
    },

    effectiveKvaDemandCharges() {
      if (this.isTimeOfUseMode && this.shouldSplitDemandByTouPeriod) {
        return this.timeOfUseDemandChargeTotal;
      }

      if (this.usesCalculatedDemandCharge) {
        const calculatedCharges = this.calculatedBill?.calculatedCharges || this.originalRecord?.calculatedCharges || {};
        return parseFloat(calculatedCharges.KVA_DEMAND_CHARGE || calculatedCharges.KVA_PER_UNIT_CHARGE || this.manualData.kvaDemandCharges || 0) || 0;
      }

      return parseFloat(this.manualData.kvaDemandCharges) || 0;
    },

    totalConsumptionForCalculation() {
      if (this.isTimeOfUseMode) {
        const timeOfUseUnits = this.manualData.timeOfUseUnits || this.consumptionData?.touBreakdown || {};
        const timeOfUseTotal = Object.values(timeOfUseUnits).reduce((sum, line) => (
          sum + (parseFloat(line?.units) || parseFloat(line?.difference) || 0)
        ), 0);

        if (timeOfUseTotal > 0) {
          return timeOfUseTotal;
        }
      }

      const directTotal = parseFloat(this.consumptionData?.totalConsumption || this.consumptionData?.total_consumption);
      if (!Number.isNaN(directTotal) && directTotal > 0) {
        return directTotal;
      }

      const timeOfUseUnits = this.manualData.timeOfUseUnits || this.consumptionData?.touBreakdown || {};
      const timeOfUseTotal = Object.values(timeOfUseUnits).reduce((sum, line) => sum + (parseFloat(line?.units) || parseFloat(line?.difference) || 0), 0);

      if (timeOfUseTotal > 0) {
        return timeOfUseTotal;
      }

      return parseFloat(this.manualData.actualKwhFromBill) || 0;
    },

    displayedCalculatedAmount() {
      if (!this.calculatedBill) return 0;
      if (!this.shouldUseBillingUnitCost) {
        return parseFloat(this.calculatedBill.calculatedCharges?.totalCalculated) || 0;
      }

      return calculateTimeOfUseTotalCalculated(this.calculatedBill);
    },
  },

  async created() {
    await this.loadRecord();
  },

  watch: {
    currentStep(step) {
      if (step === '3' && (!this.calculatedBill || this.needsRecalculation)) {
        this.scheduleRecalculateBill(0);
      }
    },
  },

  beforeDestroy() {
    if (this.recalculateTimer) {
      clearTimeout(this.recalculateTimer);
      this.recalculateTimer = null;
    }
  },

  methods: {
    ...mapActions('billing', [
      'fetchBillingRecordForEdit',
      'updateBillingRecord',
      'calculateBill',
      'clearError',
    ]),

    getCachedRecordForEdit(id) {
      return this.$store.getters['billing/getBillingRecordForEdit'](id)
        || this.$store.getters['billing/getBillingRecordById'](id)
        || null;
    },

    applyRecordToDraft(record) {
      this.originalRecord = record;
      this.consumptionData = this.buildRecordConsumptionData(record);
      this.manualData = this.buildManualDataFromRecord(record);
      this.period = {
        startDate: this.consumptionData?.billingPeriod?.startDate || null,
        endDate: this.consumptionData?.billingPeriod?.endDate || null,
      };
      this.manualReading = {
        initial: parseFloat(this.consumptionData?.initialReading) || 0,
        final: parseFloat(this.consumptionData?.finalReading) || 0,
      };
    },

    getTimeOfUseUnitsFrom(data = {}) {
      return data?.timeOfUseUnits
        || data?.consumptionData?.touBreakdown
        || data?.touBreakdown
        || {};
    },

    getTimeOfUseDemandFrom(data = {}) {
      return data?.timeOfUseDemand
        || data?.consumptionData?.demandBreakdown
        || data?.demandBreakdown
        || {};
    },

    buildRecordConsumptionData(record) {
      if (!record?.consumptionData) {
        return null;
      }

      const source = record.consumptionData;
      const timeOfUseUnits = this.getTimeOfUseUnitsFrom(record);
      const timeOfUseDemand = this.getTimeOfUseDemandFrom(record);
      const timeOfUseTotal = Object.values(timeOfUseUnits).reduce((sum, line) => sum + (parseFloat(line?.units) || parseFloat(line?.difference) || 0), 0);
      const totalConsumption = parseFloat(source.totalConsumption || source.total_consumption) || timeOfUseTotal || 0;

      return {
        ...source,
        totalConsumption,
        billingPeriod: record.billingPeriod || source.billingPeriod,
        dataSource: source.dataSource || 'manual',
        timeOfUseUnits,
        touBreakdown: this.getComparableTimeOfUseUnits(timeOfUseUnits),
        demandBreakdown: this.getComparableTimeOfUseDemand(timeOfUseDemand),
      };
    },

    buildManualDataFromRecord(record) {
      return {
        kvaDemand: parseFloat(record?.kvaDemand) || 0,
        actualBillAmount: parseFloat(record?.actualBillAmount) || 0,
        kvaDemandCharges: parseFloat(record?.kvaDemandCharges) || 0,
        actualKwhFromBill: parseFloat(record?.actualKwhFromBill) || 0,
        timeOfUseUnits: this.getTimeOfUseUnitsFrom(record) || null,
        timeOfUseDemand: this.getTimeOfUseDemandFrom(record) || null,
        oilSurchargeRate: parseFloat(record?.oilSurchargeRate) || 0,
        notes: record?.notes || '',
      };
    },

    buildCalculatedBillFromRecord(record) {
      if (!record?.calculatedCharges || !record.calculatedCharges.totalCalculated) {
        return null;
      }

      const consumptionData = this.buildRecordConsumptionData(record);
      const calculatedBill = {
        billingPeriod: record.billingPeriod,
        consumptionData,
        kvaDemand: parseFloat(record.kvaDemand) || 0,
        calculatedCharges: record.calculatedCharges,
        subtotal: getTimeOfUseSubtotalBasis(record),
        actualBillAmount: parseFloat(record.actualBillAmount) || 0,
        variance: parseFloat(record.variance) || 0,
        ratesUsed: record.ratesUsed || [],
        calculationMode: record.calculationMode || this.billingMode,
        unitCostForBilling: parseFloat(record.unitCostForBilling) || 0,
        createdBy: record.createdBy || appConfig.getParentUserName(),
        createdAt: record.createdAt || new Date(),
      };

      return {
        ...calculatedBill,
        unitCostForBilling: calculatedBill.unitCostForBilling || this.calculateUnitCostForBilling(calculatedBill),
      };
    },

    normalizeConsumptionDraft(data) {
      if (!data) {
        return null;
      }

      const nextConsumptionData = data.consumptionData || data;
      const nextBillingPeriod = data.billingPeriod || nextConsumptionData.billingPeriod || this.originalRecord?.billingPeriod;
      const timeOfUseUnits = this.getTimeOfUseUnitsFrom(nextConsumptionData);
      const timeOfUseDemand = this.getTimeOfUseDemandFrom(nextConsumptionData);

      return {
        ...nextConsumptionData,
        billingPeriod: nextBillingPeriod,
        timeOfUseUnits,
        touBreakdown: timeOfUseUnits,
        demandBreakdown: timeOfUseDemand,
      };
    },

    normalizeManualDraft(data) {
      const kvaDemand = parseFloat(data.kvaDemand);
      const actualBillAmount = parseFloat(data.actualBillAmount);
      const kvaDemandCharges = parseFloat(data.kvaDemandCharges);
      const actualKwhFromBill = parseFloat(data.actualKwhFromBill);

      return {
        kvaDemand: Number.isNaN(kvaDemand) ? 0 : kvaDemand,
        actualBillAmount: Number.isNaN(actualBillAmount) ? 0 : actualBillAmount,
        kvaDemandCharges: Number.isNaN(kvaDemandCharges) ? 0 : kvaDemandCharges,
        actualKwhFromBill: Number.isNaN(actualKwhFromBill) ? 0 : actualKwhFromBill,
        timeOfUseUnits: data.timeOfUseUnits
          || this.manualData.timeOfUseUnits,
        timeOfUseDemand: data.timeOfUseDemand
          || this.manualData.timeOfUseDemand,
        oilSurchargeRate: parseFloat(data.oilSurchargeRate) || 0,
        notes: data.notes || '',
      };
    },

    areDraftValuesEqual(left, right) {
      return JSON.stringify(left || null) === JSON.stringify(right || null);
    },

    buildComparableConsumptionDraft(data) {
      if (!data) {
        return null;
      }

      const billingPeriod = data.billingPeriod || {};
      const timeOfUseUnits = this.getTimeOfUseUnitsFrom(data);
      const timeOfUseDemand = this.getTimeOfUseDemandFrom(data);

      return {
        totalConsumption: parseFloat(data.totalConsumption || data.total_consumption) || 0,
        initialReading: data.initialReading ?? null,
        finalReading: data.finalReading ?? null,
        billingPeriod: {
          startDate: billingPeriod.startDate ? new Date(billingPeriod.startDate).getTime() : null,
          endDate: billingPeriod.endDate ? new Date(billingPeriod.endDate).getTime() : null,
        },
        touBreakdown: timeOfUseUnits,
        demandBreakdown: timeOfUseDemand,
      };
    },

    areConsumptionDraftsEqual(left, right) {
      return this.areDraftValuesEqual(
        this.buildComparableConsumptionDraft(left),
        this.buildComparableConsumptionDraft(right),
      );
    },

    getComparableTimeOfUseUnits(units = {}) {
      return Object.keys(units || {}).sort().reduce((normalized, key) => {
        const line = units[key] || {};
        const explicitUnits = parseFloat(line.units);
        const explicitDifference = parseFloat(line.difference);
        const finalReading = parseFloat(line.finalReading);
        const initialReading = parseFloat(line.initialReading);
        const calculatedDifference = !Number.isNaN(finalReading) && !Number.isNaN(initialReading)
          ? Math.max(finalReading - initialReading, 0)
          : NaN;
        let normalizedUnits = 0;

        if (!Number.isNaN(explicitUnits)) {
          normalizedUnits = Math.max(explicitUnits, 0);
        } else if (!Number.isNaN(explicitDifference)) {
          normalizedUnits = Math.max(explicitDifference, 0);
        } else if (!Number.isNaN(calculatedDifference)) {
          normalizedUnits = calculatedDifference;
        }

        normalized[key] = {
          units: normalizedUnits,
          rate: parseFloat(line.rate) || 0,
        };

        return normalized;
      }, {});
    },

    getComparableTimeOfUseDemand(demand = {}) {
      return Object.keys(demand || {}).sort().reduce((normalized, key) => {
        const line = demand[key] || {};
        normalized[key] = {
          kw: parseFloat(line.kw) || 0,
          rate: parseFloat(line.rate) || 0,
        };
        return normalized;
      }, {});
    },

    buildComparableManualDraft(data) {
      if (!data) {
        return null;
      }

      return {
        kvaDemand: parseFloat(data.kvaDemand) || 0,
        actualBillAmount: parseFloat(data.actualBillAmount) || 0,
        kvaDemandCharges: parseFloat(data.kvaDemandCharges) || 0,
        actualKwhFromBill: parseFloat(data.actualKwhFromBill) || 0,
        timeOfUseUnits: this.getComparableTimeOfUseUnits(data.timeOfUseUnits || {}),
        timeOfUseDemand: this.getComparableTimeOfUseDemand(data.timeOfUseDemand || {}),
        oilSurchargeRate: parseFloat(data.oilSurchargeRate) || 0,
        notes: data.notes || '',
      };
    },

    areManualDraftsEqual(left, right) {
      return this.areDraftValuesEqual(
        this.buildComparableManualDraft(left),
        this.buildComparableManualDraft(right),
      );
    },

    markDraftForRecalculation() {
      this.needsRecalculation = true;
      this.calculatedBill = null;
    },

    onPeriodInput(menu) {
      if (menu === 'start') {
        this.startDateMenu = false;
      } else {
        this.endDateMenu = false;
      }

      if (this.consumptionData) {
        this.consumptionData = {
          ...this.consumptionData,
          billingPeriod: { ...this.period },
        };
        this.markDraftForRecalculation();
      }
    },

    async fetchConsumptionData() {
      if (!this.period.startDate || !this.period.endDate || !this.$refs.periodSelector) {
        return;
      }

      this.loadingConsumption = true;
      this.consumptionError = null;

      try {
        this.$refs.periodSelector.billingPeriod = {
          startDate: this.period.startDate,
          endDate: this.period.endDate,
        };
        await this.$refs.periodSelector.fetchAutomaticConsumptionData();
      } catch (error) {
        this.consumptionError = error.message || 'Failed to fetch consumption data';
      } finally {
        this.loadingConsumption = false;
      }
    },

    confirmManualConsumption() {
      const consumptionData = {
        totalConsumption: this.isTimeOfUseMode ? this.touTotalUnits : this.calculatedConsumption,
        initialReading: this.isTimeOfUseMode ? null : (parseFloat(this.manualReading.initial) || 0),
        finalReading: this.isTimeOfUseMode ? null : (parseFloat(this.manualReading.final) || 0),
        billingPeriod: { ...this.period },
        dataSource: 'manual',
        touBreakdown: this.manualData.timeOfUseUnits || {},
        demandBreakdown: this.manualData.timeOfUseDemand || {},
      };

      this.onConsumptionDataRetrieved({
        billingPeriod: { ...this.period },
        consumptionData,
      });
    },

    syncTouLine(key) {
      const line = this.manualData.timeOfUseUnits?.[key];
      if (!line) return;

      const final = parseFloat(line.finalReading) || 0;
      const initial = parseFloat(line.initialReading) || 0;
      const units = Math.max(final - initial, 0);

      this.$set(this.manualData.timeOfUseUnits, key, {
        ...line,
        units,
        difference: units,
      });
      this.confirmManualConsumption();
    },

    syncDemandLine(key, rate) {
      const line = this.manualData.timeOfUseDemand?.[key] || {};
      this.$set(this.manualData.timeOfUseDemand, key, {
        ...line,
        kw: parseFloat(line.kw) || 0,
        rate,
      });
      this.markDraftForRecalculation();
    },

    formatTouUnits(key) {
      const units = parseFloat(this.manualData.timeOfUseUnits?.[key]?.units) || 0;
      return units.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatRate(rate) {
      return (parseFloat(rate) || 0).toLocaleString('en-US', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    },

    formatDemandCharge(key) {
      const line = this.manualData.timeOfUseDemand?.[key] || {};
      const kw = Math.max(parseFloat(line.kw) || 0, 0);
      const rate = parseFloat(line.rate) || 0;
      return (kw * rate).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    async loadRecord() {
      // Validate ID prop
      if (!this.id || this.id.trim() === '') {
        console.error('Invalid billing record ID:', this.id);
        this.$router.push('/billing-history');
        this.showMessage({
          type: 'error',
          message: 'Invalid billing record ID',
        });
        return;
      }

      try {
        const record = this.getCachedRecordForEdit(this.id) || await this.fetchBillingRecordForEdit(this.id);
        if (!record) {
          console.error('Billing record not found for ID:', this.id);
          this.$router.push('/billing-history');
          this.showMessage({
            type: 'error',
            message: 'Billing record not found',
          });
          return;
        }

        this.applyRecordToDraft(record);
        this.calculatedBill = this.buildCalculatedBillFromRecord(record);
        this.needsRecalculation = !this.calculatedBill;
      } catch (error) {
        console.error('Error loading record for edit:', error);
        this.showMessage({
          type: 'error',
          message: 'Failed to load billing record for editing',
        });
        // Don't redirect on error, let user try again
      }
    },

    async recalculateBill() {
      if (this.recalculateInFlight) {
        this.recalculateQueued = true;
        return;
      }

      // Validate required data before calculation
      if (!this.consumptionData
          || !this.consumptionData.billingPeriod
          || !this.consumptionData.billingPeriod.startDate
          || this.effectiveKvaDemand < 0) {
        return;
      }

      this.recalculateInFlight = true;

      try {
        // Create a clean copy of consumption data to ensure no reference issues
        const cleanConsumptionData = {
          totalConsumption: this.totalConsumptionForCalculation,
          initialReading: this.consumptionData.initialReading,
          finalReading: this.consumptionData.finalReading,
          billingPeriod: this.consumptionData.billingPeriod,
          hourlyReadings: this.consumptionData.hourlyReadings || [],
          touBreakdown: this.manualData.timeOfUseUnits || this.consumptionData.touBreakdown || {},
          demandBreakdown: this.getDemandLinesForCalculation(),
        };

        const result = await this.calculateBill({
          consumptionData: cleanConsumptionData,
          kvaDemand: this.effectiveKvaDemand,
          billingDate: new Date(this.consumptionData.billingPeriod.endDate || this.consumptionData.billingPeriod.startDate),
          billingMode: this.billingMode,
          tariffMode: this.isTimeOfUseMode ? 'TIME_OF_USE' : 'STANDARD',
          tariffData: this.buildTariffData(),
        });

        this.calculatedBill = {
          ...result,
          calculationMode: this.isTimeOfUseMode ? 'TIME_OF_USE' : (result.calculationMode || this.billingMode),
          unitCostForBilling: this.calculateUnitCostForBilling(result),
        };
        this.needsRecalculation = false;
      } catch (error) {
        console.error('Error recalculating bill:', error);
        this.showMessage({
          type: 'error',
          message: `Failed to recalculate bill: ${error.message || 'Unknown error'}`,
        });
      } finally {
        this.recalculateInFlight = false;

        if (this.recalculateQueued) {
          this.recalculateQueued = false;
          this.scheduleRecalculateBill(0);
        }
      }
    },

    onConsumptionDataRetrieved(data) {
      if (!data) {
        return;
      }

      const nextConsumptionData = this.normalizeConsumptionDraft(data);
      const consumptionChanged = !this.areConsumptionDraftsEqual(this.consumptionData, nextConsumptionData);

      this.consumptionData = nextConsumptionData;

      const timeOfUseUnits = this.getTimeOfUseUnitsFrom(nextConsumptionData);
      const timeOfUseDemand = this.getTimeOfUseDemandFrom(nextConsumptionData);
      const nextManualData = {
        ...this.manualData,
        timeOfUseUnits: Object.keys(timeOfUseUnits || {}).length ? timeOfUseUnits : this.manualData.timeOfUseUnits,
        timeOfUseDemand: Object.keys(timeOfUseDemand || {}).length ? timeOfUseDemand : this.manualData.timeOfUseDemand,
      };
      const manualChanged = !this.areManualDraftsEqual(this.manualData, nextManualData);

      this.manualData = nextManualData;

      if (consumptionChanged || manualChanged) {
        this.markDraftForRecalculation();
      }
    },

    onManualDataUpdated(data) {
      if (!data) {
        return;
      }

      const nextManualData = this.normalizeManualDraft(data);
      const manualChanged = !this.areManualDraftsEqual(this.manualData, nextManualData);

      this.manualData = nextManualData;

      if (manualChanged) {
        this.markDraftForRecalculation();
      }
    },

    scheduleRecalculateBill(delay = 450) {
      if (this.recalculateTimer) {
        clearTimeout(this.recalculateTimer);
      }

      this.recalculateTimer = setTimeout(() => {
        this.recalculateTimer = null;
        this.recalculateBill();
      }, delay);
    },

    buildTariffData() {
      if (!this.isTimeOfUseMode) {
        return null;
      }

      return {
        unitLines: this.manualData.timeOfUseUnits || this.consumptionData?.touBreakdown || {},
        demandLines: this.getDemandLinesForCalculation(),
        oilSurchargeRate: this.manualData.oilSurchargeRate || 0,
      };
    },

    calculateUnitCostForBilling() {
      if (!this.shouldUseBillingUnitCost) {
        return 0;
      }

      return calculateTimeOfUseBillingRate(this.calculatedBill);
    },

    buildConsumptionDataForSave() {
      if (!this.consumptionData) {
        return this.originalRecord?.consumptionData || null;
      }

      if (!this.isTimeOfUseMode) {
        return this.consumptionData;
      }

      const timeOfUseUnits = this.manualData.timeOfUseUnits || this.consumptionData?.touBreakdown || {};
      const timeOfUseDemand = this.shouldSplitDemandByTouPeriod
        ? (this.manualData.timeOfUseDemand || this.consumptionData?.demandBreakdown || {})
        : {};

      return {
        ...this.consumptionData,
        totalConsumption: this.totalConsumptionForCalculation,
        touBreakdown: timeOfUseUnits,
        demandBreakdown: timeOfUseDemand,
      };
    },

    getDemandLinesForCalculation() {
      if (this.shouldSplitDemandByTouPeriod) {
        return this.manualData.timeOfUseDemand || this.consumptionData?.demandBreakdown || {};
      }

      const kvaDemand = parseFloat(this.manualData.kvaDemand) || 0;
      return kvaDemand > 0
        ? {}
        : {};
    },

    async onSaveSuccess(updatedRecord) {
      if (updatedRecord) {
        this.applyRecordToDraft(updatedRecord);
        this.calculatedBill = this.buildCalculatedBillFromRecord(updatedRecord);
        this.needsRecalculation = false;
      }
      this.showSuccessDialog = true;
    },

    onSaveError(error) {
      this.showMessage({
        type: 'error',
        message: error.message || 'Failed to update billing record',
      });
    },

    goToReviewStep() {
      if (this.currentStep !== '3') {
        this.currentStep = '3';
      }
      if (!this.calculatedBill || this.needsRecalculation) {
        this.scheduleRecalculateBill(0);
      }
      this.editStep = 2;
    },

    nextStep() {
      if (this.stepNumber >= 4) {
        return;
      }

      this.currentStep = String(this.stepNumber + 1);
    },

    previousStep() {
      if (this.stepNumber > 1) {
        this.currentStep = String(this.stepNumber - 1);
      }
    },

    cancelEdit() {
      this.$router.go(-1); // Go back to previous page
    },

    goToDetail() {
      if (!this.originalRecord) {
        this.goToHistory();
        return;
      }
      const recordId = this.originalRecord._id || this.originalRecord.id;
      this.$router.push(`/billing-history/${recordId}`);
    },

    goToHistory() {
      this.$router.push('/billing-history');
    },

    showMessage(message) {
      this.$emit('show-message', message);
    },

    formatDateTime(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString();
    },
  },
};
</script>

<style scoped>
.field-section-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0,0,0,0.45);
  margin-bottom: 6px;
}

.tou-table {
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 4px;
}

.tou-input {
  max-width: 120px;
  margin: 2px auto;
}
</style>
